import React from 'react';
import { ArrowLeft, TrendingUp, TrendingDown, BarChart3, DollarSign, Target, Percent, Clock, AlertTriangle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTradingMetrics, useTradingMetricsHistory, useTradingTrades, useCalibrationReport, useAccountLatest, useAccountHistory } from '../hooks/useTradingAnalysis';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Line, Area, ComposedChart,
} from 'recharts';
import TradeTable from '../components/trading/TradeTable';

const COLORS = {
  win: '#34d399',
  loss: '#f87171',
  neutral: '#94a3b8',
  teal: '#2dd4bf',
  amber: '#fbbf24',
};

const TradingPerformance: React.FC = () => {
  const navigate = useNavigate();
  const { data: metrics } = useTradingMetrics();
  const { data: history = [] } = useTradingMetricsHistory(30);
  const { data: trades = [] } = useTradingTrades();
  const { data: calibration } = useCalibrationReport();
  const { data: account } = useAccountLatest();
  const { data: accountHistory = [] } = useAccountHistory(30);

  // Win/Loss pie data
  const winLossData = metrics
    ? [
        { name: 'Winners', value: metrics.winning_trades, color: COLORS.win },
        { name: 'Losers', value: metrics.losing_trades, color: COLORS.loss },
      ]
    : [];

  // P/L per trade for bar chart
  const pnlData = trades
    .filter((t) => t.profit_loss !== null)
    .slice(0, 30)
    .reverse()
    .map((t) => ({
      ticket: `#${t.ticket}`,
      pnl: t.profit_loss || 0,
      color: (t.profit_loss || 0) >= 0 ? COLORS.win : COLORS.loss,
    }));

  // Daily P/L from history
  const dailyPnlData = history
    .filter((h) => h.total_trades > 0)
    .map((h) => ({
      date: h.date.slice(5), // MM-DD
      pnl: h.net_profit,
      color: h.net_profit >= 0 ? COLORS.win : COLORS.loss,
    }));

  // Cumulative P/L + drawdown from history
  const equityDrawdownData = history.map((h) => {
    // Calculate drawdown from cumulative
    const peak = Math.max(...history.filter((_, i) => i <= history.indexOf(h)).map((x) => x.cumulative_profit));
    const dd = peak - h.cumulative_profit;
    return {
      date: h.date.slice(5),
      equity: h.cumulative_profit,
      drawdown: -dd,
    };
  });

  // API cost vs profit from history
  const costVsProfitData = history
    .filter((h) => h.total_trades > 0 || h.api_cost > 0)
    .map((h) => ({
      date: h.date.slice(5),
      profit: h.net_profit,
      cost: h.api_cost,
    }));

  // Balance/equity history chart data
  const balanceEquityData = accountHistory.map((s) => ({
    date: s.timestamp.slice(5, 16).replace('T', ' '),
    balance: s.balance,
    equity: s.equity,
  }));

  const statCards = metrics
    ? [
        { label: 'Total Trades', value: metrics.total_trades, icon: BarChart3, color: 'text-white' },
        { label: 'Win Rate', value: metrics.win_rate !== null ? `${metrics.win_rate.toFixed(1)}%` : '—', icon: Target, color: (metrics.win_rate || 0) >= 50 ? 'text-emerald-400' : 'text-red-400' },
        { label: 'Net P/L', value: `$${metrics.net_profit.toFixed(2)}`, icon: metrics.net_profit >= 0 ? TrendingUp : TrendingDown, color: metrics.net_profit >= 0 ? 'text-emerald-400' : 'text-red-400' },
        { label: 'Expectancy', value: metrics.expectancy !== null ? `$${metrics.expectancy.toFixed(2)}` : '—', icon: TrendingUp, color: (metrics.expectancy || 0) >= 0 ? 'text-emerald-400' : 'text-red-400' },
        { label: 'Profit Factor', value: metrics.profit_factor !== null ? metrics.profit_factor.toFixed(2) : '—', icon: Percent, color: (metrics.profit_factor || 0) >= 1.5 ? 'text-emerald-400' : 'text-amber-400' },
        { label: 'Max Drawdown', value: metrics.max_drawdown !== null ? `$${metrics.max_drawdown.toFixed(2)}` : '—', icon: AlertTriangle, color: 'text-red-400' },
        { label: 'Current DD %', value: metrics.current_drawdown_pct !== null ? `${metrics.current_drawdown_pct.toFixed(1)}%` : '—', icon: AlertTriangle, color: (metrics.current_drawdown_pct || 0) > 5 ? 'text-red-400' : 'text-amber-400' },
        { label: 'Days Since DD', value: metrics.days_since_max_drawdown !== null ? `${metrics.days_since_max_drawdown}d` : '—', icon: Clock, color: 'text-slate-300' },
        { label: 'Avg Duration', value: metrics.avg_trade_duration !== null ? `${metrics.avg_trade_duration.toFixed(0)}m` : '—', icon: Clock, color: 'text-slate-300' },
        { label: 'API Cost', value: `$${metrics.total_api_cost.toFixed(4)}`, icon: DollarSign, color: 'text-slate-300' },
      ]
    : [];

  const tooltipStyle = { backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' };

  return (
    <div className="p-6 space-y-6 pb-20 lg:pb-0">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/trading')} className="p-2 hover:bg-slate-700 rounded-lg transition-colors">
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-white">Performance</h1>
          <p className="text-slate-400 text-sm">Trading performance metrics and analytics</p>
        </div>
      </div>

      {/* Account Stat Cards */}
      {account && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign size={14} className="text-slate-400" />
              <span className="text-xs text-slate-400">Balance</span>
            </div>
            <p className="text-lg font-bold text-white">${account.balance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <DollarSign size={14} className="text-slate-400" />
              <span className="text-xs text-slate-400">Equity</span>
            </div>
            <p className="text-lg font-bold text-white">${account.equity.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
          </div>
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp size={14} className="text-slate-400" />
              <span className="text-xs text-slate-400">Daily P/L</span>
            </div>
            <p className={`text-lg font-bold ${(account.daily_pnl ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {(account.daily_pnl ?? 0) >= 0 ? '+' : ''}${(account.daily_pnl ?? 0).toFixed(2)}
            </p>
          </div>
          <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp size={14} className="text-slate-400" />
              <span className="text-xs text-slate-400">Weekly P/L</span>
            </div>
            <p className={`text-lg font-bold ${(account.weekly_pnl ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
              {(account.weekly_pnl ?? 0) >= 0 ? '+' : ''}${(account.weekly_pnl ?? 0).toFixed(2)}
            </p>
          </div>
        </div>
      )}

      {/* Balance & Equity History */}
      {balanceEquityData.length > 0 && (
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5">
          <h3 className="text-sm font-medium text-slate-400 mb-4">Account Balance & Equity</h3>
          <div className="h-48 lg:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={balanceEquityData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis stroke="#64748b" tickFormatter={(v) => `$${v.toLocaleString()}`} />
                <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [`$${value.toLocaleString(undefined, { minimumFractionDigits: 2 })}`, undefined]} />
                <Line type="monotone" dataKey="balance" stroke={COLORS.teal} strokeWidth={2} dot={false} name="Balance" />
                <Line type="monotone" dataKey="equity" stroke={COLORS.teal} strokeWidth={1.5} strokeDasharray="5 5" dot={false} name="Equity" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Stat Cards */}
      {metrics && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {statCards.map((card) => {
            const Icon = card.icon;
            return (
              <div key={card.label} className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Icon size={14} className="text-slate-400" />
                  <span className="text-xs text-slate-400">{card.label}</span>
                </div>
                <p className={`text-lg font-bold ${card.color}`}>{card.value}</p>
              </div>
            );
          })}
        </div>
      )}

      {/* Confidence Accuracy & Signal Distribution */}
      {metrics && (metrics.confidence_accuracy !== null || metrics.signal_distribution) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {metrics.confidence_accuracy !== null && (
            <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5">
              <h3 className="text-sm font-medium text-slate-400 mb-3">Confidence Accuracy</h3>
              <p className="text-3xl font-bold text-white">{metrics.confidence_accuracy > 0 ? '+' : ''}{metrics.confidence_accuracy}</p>
              <p className="text-xs text-slate-500 mt-1">
                Avg confidence of winners minus avg confidence of losers.
                Positive = higher confidence correlates with wins.
              </p>
            </div>
          )}
          {metrics.signal_distribution && (
            <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5">
              <h3 className="text-sm font-medium text-slate-400 mb-3">Signal Distribution</h3>
              <div className="flex gap-4 mt-2">
                {Object.entries(metrics.signal_distribution).map(([sig, count]) => (
                  <div key={sig} className="text-center">
                    <p className={`text-2xl font-bold ${
                      sig === 'BUY' ? 'text-emerald-400' : sig === 'SELL' ? 'text-red-400' : 'text-amber-400'
                    }`}>{count}</p>
                    <p className="text-xs text-slate-400">{sig}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Equity Curve with Drawdown */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5">
          <h3 className="text-sm font-medium text-slate-400 mb-4">Equity Curve & Drawdown</h3>
          {equityDrawdownData.length > 0 ? (
            <div className="h-48 lg:h-72">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={equityDrawdownData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis stroke="#64748b" tickFormatter={(v) => `$${v}`} />
                  <Tooltip contentStyle={tooltipStyle} labelStyle={{ color: '#94a3b8' }} />
                  <Area type="monotone" dataKey="drawdown" fill="#f8717120" stroke="#f87171" strokeWidth={1} name="Drawdown" />
                  <Line type="monotone" dataKey="equity" stroke={COLORS.teal} strokeWidth={2} dot={false} name="Equity" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 lg:h-72 flex items-center justify-center text-slate-500 text-sm">No data available</div>
          )}
        </div>

        {/* Win/Loss Distribution */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5">
          <h3 className="text-sm font-medium text-slate-400 mb-4">Win/Loss Distribution</h3>
          {winLossData.length > 0 && (winLossData[0].value > 0 || winLossData[1].value > 0) ? (
            <div className="h-48 lg:h-72">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={winLossData} cx="50%" cy="50%" innerRadius={60} outerRadius={100} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                    {winLossData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 lg:h-72 flex items-center justify-center text-slate-500 text-sm">No data available</div>
          )}
        </div>

        {/* Daily P/L */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5">
          <h3 className="text-sm font-medium text-slate-400 mb-4">Daily P/L</h3>
          {dailyPnlData.length > 0 ? (
            <div className="h-48 lg:h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={dailyPnlData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis stroke="#64748b" tickFormatter={(v) => `$${v}`} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [`$${value.toFixed(2)}`, 'P/L']} />
                  <Bar dataKey="pnl" name="Daily P/L">
                    {dailyPnlData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 lg:h-72 flex items-center justify-center text-slate-500 text-sm">No data available</div>
          )}
        </div>

        {/* API Cost vs Trading Profit */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5">
          <h3 className="text-sm font-medium text-slate-400 mb-4">API Cost vs Trading Profit</h3>
          {costVsProfitData.length > 0 ? (
            <div className="h-48 lg:h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={costVsProfitData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="date" tick={{ fill: '#64748b', fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis stroke="#64748b" tickFormatter={(v) => `$${v}`} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar dataKey="profit" name="Profit" fill={COLORS.teal} />
                  <Bar dataKey="cost" name="API Cost" fill={COLORS.amber} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 lg:h-72 flex items-center justify-center text-slate-500 text-sm">No data available</div>
          )}
        </div>

        {/* P/L per Trade */}
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5 lg:col-span-2">
          <h3 className="text-sm font-medium text-slate-400 mb-4">P/L per Trade (last 30)</h3>
          {pnlData.length > 0 ? (
            <div className="h-48 lg:h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={pnlData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="ticket" tick={false} />
                  <YAxis stroke="#64748b" tickFormatter={(v) => `$${v}`} />
                  <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [`$${value.toFixed(2)}`, 'P/L']} />
                  <Bar dataKey="pnl">
                    {pnlData.map((entry, idx) => <Cell key={idx} fill={entry.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-48 lg:h-72 flex items-center justify-center text-slate-500 text-sm">No data available</div>
          )}
        </div>
      </div>

      {/* Confidence Calibration */}
      {calibration && (calibration.confidence_buckets.length > 0 || calibration.directional_accuracy.length > 0) && (
        <>
          <h2 className="text-lg font-semibold text-white">Confidence Calibration</h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Confidence vs Win Rate */}
            {calibration.confidence_buckets.length > 0 && (
              <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-5">
                <h3 className="text-sm font-medium text-slate-400 mb-4">Stated Confidence vs Actual Win Rate</h3>
                <div className="h-48 lg:h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={calibration.confidence_buckets.map((b) => ({
                      range: b.range,
                      stated: b.stated_confidence,
                      actual: b.actual_win_rate,
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                      <XAxis dataKey="range" tick={{ fill: '#64748b', fontSize: 11 }} />
                      <YAxis stroke="#64748b" tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(value: number) => [`${value.toFixed(1)}%`]} />
                      <Bar dataKey="stated" name="Stated Confidence" fill="#94a3b8" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="actual" name="Actual Win Rate" fill={COLORS.teal} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Directional Accuracy + Overconfidence */}
            <div className="space-y-4">
              {calibration.directional_accuracy.length > 0 && (
                <div className="grid grid-cols-2 gap-4">
                  {calibration.directional_accuracy.map((d) => (
                    <div key={d.direction} className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
                      <span className="text-xs text-slate-400">{d.direction} Accuracy</span>
                      <p className={`text-2xl font-bold ${d.accuracy >= 50 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {d.accuracy.toFixed(1)}%
                      </p>
                      <p className="text-xs text-slate-500">{d.correct}/{d.total} correct</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Overconfidence Score */}
              <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
                <span className="text-xs text-slate-400">Overconfidence Score</span>
                <p className={`text-2xl font-bold ${
                  calibration.overconfidence_score > 10 ? 'text-red-400' :
                  calibration.overconfidence_score > 5 ? 'text-amber-400' :
                  calibration.overconfidence_score < -5 ? 'text-blue-400' :
                  'text-emerald-400'
                }`}>
                  {calibration.overconfidence_score > 0 ? '+' : ''}{calibration.overconfidence_score.toFixed(1)}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {calibration.overconfidence_score > 10 ? 'Significantly overconfident — stated confidence exceeds actual win rate' :
                   calibration.overconfidence_score > 5 ? 'Slightly overconfident' :
                   calibration.overconfidence_score < -5 ? 'Underconfident — actual results better than stated confidence' :
                   'Well calibrated'}
                </p>
              </div>

              {/* Rating Distribution */}
              {Object.keys(calibration.rating_distribution).length > 0 && (
                <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
                  <span className="text-xs text-slate-400 block mb-2">Rating Distribution</span>
                  <div className="flex gap-4">
                    {Object.entries(calibration.rating_distribution).map(([rating, count]) => {
                      const rColor: Record<string, string> = {
                        correct: 'text-emerald-400',
                        incorrect: 'text-red-400',
                        partial: 'text-amber-400',
                        unrated: 'text-slate-400',
                      };
                      return (
                        <div key={rating} className="text-center">
                          <p className={`text-xl font-bold ${rColor[rating] || 'text-slate-300'}`}>{count}</p>
                          <p className="text-xs text-slate-500 capitalize">{rating}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Trade History */}
      <div>
        <h2 className="text-sm font-medium text-slate-400 mb-3">Full Trade History</h2>
        <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 p-4">
          <TradeTable trades={trades} />
        </div>
      </div>
    </div>
  );
};

export default TradingPerformance;
